import { keyBy } from 'lodash-unified';
import { ROULETTE_OPPOSITE_OPTIONS, RouletteOptionsClasses } from 'betgames-contract-js';
import { GameId } from '@bg-shared/enums/GameId';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { t } from '@bg-shared/business/Translate';
import {
    ICasinoRecentActionsModel,
    ICasinoValidationError,
    ICasinoValidationParams,
} from '@bg-shared/business/CasinoBetting/interfaces';
import { CasinoValidationService } from '@bg-shared/business/CasinoBetting/service/CasinoValidation.service';
import {
    casinoAutoPlayEntity,
    casinoAutoPlayService,
    optionIDConverter,
} from '@bg-shared/business/CasinoBetting';
import { IGameOptions } from '@bg-shared/business/GamesOptions/interfaces';
import { IOption } from '@bg-shared/interfaces';

export class RouletteValidationService extends CasinoValidationService {
    protected oppositeOptionsIdsMap: Record<string, string> = {};

    protected readonly groupedOptions: Record<IOption['optionClass'], IOption>;

    private readonly MAX_RESTRICTED_MULTI_BETS_ALLOWED = 2;

    constructor(
        private readonly gameId: GameId,
        bettingOptions: IGameOptions['options'],
    ) {
        super(gameId);

        this.groupedOptions = keyBy(bettingOptions, 'optionClass');

        if (partnerSettingsStore.isRouletteOppositeBettingRestricted) {
            this.buildOppositeOptionsIdsMap();

            this.defaultAddValidators.push(this.validateOppositeBet);
            this.defaultMoveValidators.push(this.validateOppositeBet);
        }

        if (partnerSettingsStore.isOutsideBetsRestricted) {
            this.defaultAddValidators.push(this.validateOppositeMultiBet);
            this.defaultMoveValidators.push(this.validateOppositeMultiBet);
        }
    }

    protected buildOppositeOptionsIdsMap() {
        if (!Object.keys(this.groupedOptions).length) {
            return;
        }

        Object.entries(ROULETTE_OPPOSITE_OPTIONS).forEach(([optionClass, oppositeOptionClass]) => {
            this.oppositeOptionsIdsMap[this.groupedOptions[optionClass].id] =
                this.groupedOptions[oppositeOptionClass].id.toString();
        });
    }

    // Validation for opposite EVEN/OOD, RED/BLACK, LOW/HIGH bets
    private validateOppositeBet(params: ICasinoValidationParams): ICasinoValidationError {
        const oppositeOptionId = this.oppositeOptionsIdsMap[params.optionItemID];
        const oppositeHasBet = this.betAmountsStore.value[oppositeOptionId] > 0;

        const isMovingFromOpposite =
            oppositeHasBet &&
            params.moveFromOptionItemID &&
            params.moveFromOptionItemID === oppositeOptionId;

        if (oppositeHasBet && !isMovingFromOpposite) {
            return {
                ...this.defaultValidationError,
                message: t.string('cannot_place_bets_on_both_outcomes'),
            };
        }

        return null;
    }

    // Validation for opposite COLUMN/DOZEN bets (max 2 of 3 allowed)
    private validateOppositeMultiBet(params: ICasinoValidationParams): ICasinoValidationError {
        const optionId = optionIDConverter.parse(params.optionItemID).oddId;

        const placedBets = Object.keys(this.betAmountsStore.value).reduce(
            (acc, optionItemID) => {
                const parsedId = optionIDConverter.parse(optionItemID).oddId;

                if (parsedId === this.groupedOptions[RouletteOptionsClasses.COLUMN].id) {
                    acc[RouletteOptionsClasses.COLUMN].push(optionItemID);
                } else if (parsedId === this.groupedOptions[RouletteOptionsClasses.DOZEN].id) {
                    acc[RouletteOptionsClasses.DOZEN].push(optionItemID);
                }
                return acc;
            },
            { [RouletteOptionsClasses.COLUMN]: [], [RouletteOptionsClasses.DOZEN]: [] },
        );

        const isColumnBet = optionId === this.groupedOptions[RouletteOptionsClasses.COLUMN].id;
        const isDozenBet = optionId === this.groupedOptions[RouletteOptionsClasses.DOZEN].id;

        const isNewBet = (betType: RouletteOptionsClasses.COLUMN | RouletteOptionsClasses.DOZEN) =>
            !placedBets[betType].includes(params.optionItemID) &&
            !placedBets[betType].includes(params.moveFromOptionItemID);

        if (
            (isColumnBet &&
                isNewBet(RouletteOptionsClasses.COLUMN) &&
                placedBets[RouletteOptionsClasses.COLUMN].length ===
                    this.MAX_RESTRICTED_MULTI_BETS_ALLOWED) ||
            (isDozenBet &&
                isNewBet(RouletteOptionsClasses.DOZEN) &&
                placedBets[RouletteOptionsClasses.DOZEN].length ===
                    this.MAX_RESTRICTED_MULTI_BETS_ALLOWED)
        ) {
            return {
                ...this.defaultValidationError,
                message: t.string('cannot_place_bets_on_all_three_outcomes'),
            };
        }

        return null;
    }

    private stopAutoPlayIfActive() {
        const autoplayStore = casinoAutoPlayEntity.store(this.gameId);

        if (autoplayStore.value.isActive) {
            casinoAutoPlayService.stopAutoPlay(autoplayStore);
        }
    }

    private validateOppositeBetsRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        const betClasses = bets.map((bet) => bet.oddClass);
        const hasOppositeBet = betClasses.some((betClass) => {
            const oppositeBetClass = ROULETTE_OPPOSITE_OPTIONS[betClass as RouletteOptionsClasses];
            return oppositeBetClass && betClasses.includes(oppositeBetClass);
        });

        if (hasOppositeBet) {
            super.showError(t.string('cannot_place_bets_on_both_outcomes'));
            this.stopAutoPlayIfActive();

            return false;
        }

        return true;
    }

    private validateOppositeMultiBetsRepeat(
        bets: ICasinoRecentActionsModel['recentBets'],
    ): boolean {
        const dozensCount = bets.filter(
            (bet) => bet.oddClass === RouletteOptionsClasses.DOZEN,
        ).length;
        const columnsCount = bets.filter(
            (bet) => bet.oddClass === RouletteOptionsClasses.COLUMN,
        ).length;

        if (
            dozensCount > this.MAX_RESTRICTED_MULTI_BETS_ALLOWED ||
            columnsCount > this.MAX_RESTRICTED_MULTI_BETS_ALLOWED
        ) {
            super.showError(t.string('cannot_place_bets_on_all_three_outcomes'));
            this.stopAutoPlayIfActive();

            return false;
        }

        return true;
    }

    // Overrides parent method to add opposite/outside bets validation
    public validateRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        return !(
            !super.validateRepeat(bets) ||
            (partnerSettingsStore.isOutsideBetsRestricted &&
                !this.validateOppositeMultiBetsRepeat(bets)) ||
            (partnerSettingsStore.isRouletteOppositeBettingRestricted &&
                !this.validateOppositeBetsRepeat(bets))
        );
    }
}
